import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious
} from "@/components/ui/pagination";
import { BASE_URL } from "@/config/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Loader2 } from "lucide-react";

const InvoicesRecurring = () => {

  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [filter, setFilter] = useState<"active" | "ended">("active");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { toast } = useToast();
  const { t } = useLanguage();

  const translateStr = (key: string): string => {
    const translated = t(key);
    return Array.isArray(translated) ? translated.join(" ") : translated;
  };

  useEffect(() => {
    fetchRecurringInvoices(currentPage);
  }, [currentPage, user]);

  const fetchRecurringInvoices = async (page: number) => {

    if (!user) return;

    setLoading(true);

    try {

      const response = await fetch(
        `${BASE_URL}/api/invoices/recurring/get?page=${page}&limit=10`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const data = await response.json();

      if (response.ok && data?.invoices) {

        setInvoices(data.invoices);
        setTotalPages(data.totalPages);
        setCurrentPage(data.currentPage);

      } else {

        setInvoices([]);

        toast({
          title: translateStr("invoices.recurring.fetchErrorTitle"),
          description:
            data.message ||
            translateStr("invoices.recurring.fetchErrorDescription"),
          variant: "destructive"
        });

      }

    } catch (error) {

      toast({
        title: translateStr("invoices.recurring.fetchErrorTitle"),
        description: translateStr("invoices.recurring.fetchErrorDescription"),
        variant: "destructive"
      });

    } finally {
      setLoading(false);
    }

  };

  const handleStopRecurrence = async (invoiceId: string) => {

    try {

      const response = await fetch(
        `${BASE_URL}/api/invoices/recurring/stop`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ invoiceId })
        }
      );

      if (response.ok) {

        toast({
          title: translateStr("invoices.recurring.stopSuccessTitle"),
          description: translateStr(
            "invoices.recurring.stopSuccessDescription"
          )
        });

        fetchRecurringInvoices(currentPage);

      } else {
        throw new Error();
      }

    } catch {

      toast({
        title: translateStr("invoices.recurring.stopErrorTitle"),
        description: translateStr(
          "invoices.recurring.stopErrorDescription"
        ),
        variant: "destructive"
      });

    }

  };

  const filteredInvoices = invoices.filter((invoice) =>
    filter === "active"
      ? invoice.isRecurringValid
      : !invoice.isRecurringValid
  );

  const handlePageChange = (page: number) => {

    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }

  };

  return (

    <div className="min-h-screen flex flex-col">

      <Navbar />

      <main className="flex-1 container py-8 mt-16">

        <Card>

          <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">

            <CardTitle>
              {translateStr("invoices.recurringInvoices")}
            </CardTitle>

            <div className="flex gap-2">

              <Button
                variant={filter === "active" ? "default" : "outline"}
                onClick={() => setFilter("active")}
              >
                {translateStr("invoices.recurringInProgress")}
              </Button>

              <Button
                variant={filter === "ended" ? "default" : "outline"}
                onClick={() => setFilter("ended")}
              >
                {translateStr("invoices.recurringStopped")}
              </Button>

            </div>

          </CardHeader>

          <CardContent>

            {/* MOBILE VIEW */}

            <div className="md:hidden space-y-4">

              {loading && (
                <div className="flex justify-center">
                  <Loader2 className="animate-spin" />
                </div>
              )}

              {!loading && filteredInvoices.length === 0 && (
                <div className="text-center text-sm text-muted-foreground">
                  {translateStr("invoices.recurring.noInvoices")}
                </div>
              )}

              {filteredInvoices.map((invoice) => (

                <Card
                  key={invoice.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/invoices/${invoice.id}`)}
                >

                  <CardContent className="p-4 space-y-2 text-sm">

                    <div className="flex justify-between">
                      <span className="font-medium">
                        {translateStr("invoices.student")}
                      </span>
                      <span>{invoice.student}</span>
                    </div>

                    <div className="flex justify-between">
                      <span className="font-medium">
                        {translateStr("invoices.amount")}
                      </span>
                      <span>
                        {invoice.amount} {invoice.invoice_currency}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="font-medium">
                        {translateStr("invoices.date")}
                      </span>
                      <span>
                        {new Date(invoice.date).toLocaleDateString()}
                      </span>
                    </div>

                    <div className="flex justify-between bg-gray-50 px-1">
                      <span className="font-medium">
                        {translateStr("agenda.frequency")}
                      </span>
                      <span>
                      {invoice.recurrence_frequency ? t('agenda.' + invoice.recurrence_frequency.toLocaleLowerCase()) : '-'}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="font-medium">
                        {t('common.next')}
                      </span>
                      <span>
                        {new Date(
                          invoice.next_recurring_date
                        ).toLocaleDateString()}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="font-medium">
                        {t('invoices.lastInvoiceDate')}
                      </span>
                      <span>
                        {new Date(
                          invoice.date_recurring
                        ).toLocaleDateString()}
                      </span>
                    </div>

                    {invoice.isRecurringValid && (
                      <Button
                        size="sm"
                        variant="destructive"
                        className="w-full mt-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStopRecurrence(invoice.id);
                        }}
                      >
                        {translateStr(
                          "invoices.recurring.stopRecurrence"
                        )}
                      </Button>
                    )}

                  </CardContent>

                </Card>

              ))}

            </div>

            {/* DESKTOP TABLE */}

            <div className="hidden md:block">

              <Table>

                <TableHeader>

                  <TableRow>

                    <TableHead>
                      {translateStr("invoices.date")}
                    </TableHead>

                    <TableHead>
                      {translateStr("invoices.student")}
                    </TableHead>

                    <TableHead>
                      {translateStr("invoices.amount")}
                    </TableHead>

                    <TableHead>
                      {translateStr("invoices.recurring.nextDate")}
                    </TableHead>

                    <TableHead>
                      {translateStr("invoices.lastInvoiceDate")}
                    </TableHead>

                     <TableHead>
                      {translateStr("agenda.frequency")}
                    </TableHead>

                    <TableHead>
                      {translateStr("invoices.recurring.invoiceActions")}
                    </TableHead> 

                  </TableRow>

                </TableHeader>

                <TableBody>

                  {loading ? (

                    <TableRow>

                      <TableCell colSpan={7} className="text-center">
                        <Loader2 className="animate-spin inline-block" />
                      </TableCell>

                    </TableRow>

                  ) : filteredInvoices.length > 0 ? (

                    filteredInvoices.map((invoice) => (

                      <TableRow
                        key={invoice.id}
                        className="cursor-pointer"
                        onClick={() =>
                          navigate(`/invoices/${invoice.id}`)
                        }
                      >

                        <TableCell>
                          {new Date(invoice.date).toLocaleDateString()}
                        </TableCell>

                        <TableCell>
                          {invoice.student}
                        </TableCell>

                        <TableCell>
                          {invoice.amount} {invoice.invoice_currency}
                        </TableCell>

                        <TableCell>
                          {new Date(
                            invoice.next_recurring_date
                          ).toLocaleDateString()}
                        </TableCell>

                        <TableCell>
                          {new Date(
                            invoice.date_recurring
                          ).toLocaleDateString()}
                        </TableCell>

                        <TableCell>
                          {invoice.recurrence_frequency ? t('agenda.' + invoice.recurrence_frequency.toLocaleLowerCase()) : '-'}
                        </TableCell>

                        <TableCell>

                          {invoice.isRecurringValid ? (

                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStopRecurrence(invoice.id);
                              }}
                            >

                              {translateStr(
                                "invoices.recurring.stopRecurrence"
                              )}

                            </Button>

                          ) : (

                            <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">

                              {translateStr(
                                "invoices.recurringInvalid"
                              )}

                            </span>

                          )}

                        </TableCell>

                      </TableRow>

                    ))

                  ) : (

                    <TableRow>

                      <TableCell colSpan={7} className="text-center">
                        {translateStr("invoices.recurring.noInvoices")}
                      </TableCell>

                    </TableRow>

                  )}

                </TableBody>

              </Table>

            </div>

            {/* PAGINATION */}

            <div className="flex justify-center mt-6 border-t pt-4">

              <Pagination>

                <PaginationContent>

                  <PaginationItem>

                    <PaginationPrevious
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(currentPage - 1);
                      }}
                    />

                  </PaginationItem>

                  {[...Array(totalPages).keys()].map((page) => (

                    <PaginationItem key={page + 1}>

                      <PaginationLink
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(page + 1);
                        }}
                        isActive={currentPage === page + 1}
                      >

                        {page + 1}

                      </PaginationLink>

                    </PaginationItem>

                  ))}

                  <PaginationItem>

                    <PaginationNext
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(currentPage + 1);
                      }}
                    />

                  </PaginationItem>

                </PaginationContent>

              </Pagination>

            </div>

          </CardContent>

        </Card>

      </main>

      <Footer />

    </div>

  );

};

export default InvoicesRecurring;